import { useEffect, useMemo, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { Button, Segmented, Typography } from "antd";
import { AppstoreOutlined, LockOutlined, PlayCircleOutlined, TableOutlined, UnorderedListOutlined } from "@ant-design/icons";
import { audioMessage as message } from "@components/Common/Notifications/audioMessage";
import { useUserPermissions } from "@components/PermissionedComponents";
import {
    DEFAULT_PRESET_NAME,
    DEFAULT_VISUALIZER_COLUMNS,
    DEFAULT_VISUALIZER_OPTIONS,
    EMPTY_NOTE_FILTERS,
    filterNotes,
    resolveNoteFilters,
} from "@mappers/notes.mapper.mjs";
import type { NoteFilterState, VisualizerColumn, VisualizerOptions } from "@mappers/notes.mapper.mjs";
import { useCanViewNotes } from "@hooks/useCanViewNotes";
import { deleteNote, getNoteAttributes, getNotes, getNoteTags } from "@store/actions/notes.actions";
import type { Note, NotesState } from "@store/reducers/notes.reducer";
import { readNoteApiError } from "@utils/noteApiError.mjs";
import { defaultNotesRange } from "@utils/notesDates.mjs";
import { AttributeSettings } from "./AttributeSettings";
import { TagSettings } from "./TagSettings";
import { NoteDialog } from "./NoteDialog";
import type { NoteDialogRequest } from "./NoteDialog";
import { NotesFilters } from "./NotesFilters";
import type { NotesRange } from "./NotesFilters";
import { NotesList } from "./NotesList";
import { NotesTutorial } from "./NotesTutorial";
import { Visualizer } from "./Visualizer/Visualizer";
import "@styles/styles-Notes.css";

type NotesView = "notes" | "notes-2col" | "visualizer" | "attributes";

const VIEWS = [
    { value: "notes" as NotesView, icon: <UnorderedListOutlined />, title: "List View" },
    { value: "notes-2col" as NotesView, icon: <AppstoreOutlined />, title: "List View, two columns" },
    { value: "visualizer" as NotesView, icon: <TableOutlined />, title: "Table View" },
];

interface NotesTabProps {
    /** Set on a Player page. It fixes the player filter to that player. */
    fixedPlayerId?: number;
}

// A thunk shows its own error unless the error names an input. No input of this tab can show
// a field error, so this tab shows it as a message.
const showFieldError = (e: unknown) => {
    const { message: text, field } = readNoteApiError(e);
    if (field !== null) message.error(text);
};

const NotesTabContent = ({ fixedPlayerId }: NotesTabProps) => {
    const dispatch = useDispatch<any>();
    const timeZone: string = useSelector(({ team }: any) => team.info.home_timezone);
    const notes = useSelector((state: { notes: NotesState }) => state.notes.notes);
    const loading = useSelector((state: { notes: NotesState }) => state.notes.loading.notes);

    const permissions = useUserPermissions();
    const canWrite = permissions?.WriteNotes;

    const [view, setView] = useState<NotesView>("notes");
    // The request stays set while the dialog closes, so the title does not change during the fade-out.
    const [dialog, setDialog] = useState<{ open: boolean; request: NoteDialogRequest }>({ open: false, request: {} });
    const [tutorialOpen, setTutorialOpen] = useState(false);
    // The columns of the Visualizer live here, so a switch of the view keeps them. A reload resets them.
    const [columns, setColumns] = useState<VisualizerColumn[]>(DEFAULT_VISUALIZER_COLUMNS);
    const [presetName, setPresetName] = useState(DEFAULT_PRESET_NAME);
    const [options, setOptions] = useState<VisualizerOptions>(DEFAULT_VISUALIZER_OPTIONS);
    const [filters, setFilters] = useState<NoteFilterState>(EMPTY_NOTE_FILTERS);
    const [range, setRange] = useState<NotesRange>(() => defaultNotesRange(timeZone));

    // The thunks show their own errors. The `catch` only stops an unhandled rejection.
    useEffect(() => {
        dispatch(getNoteTags()).catch(() => {});
        dispatch(getNoteAttributes()).catch(() => {});
    }, [dispatch]);

    useEffect(() => {
        dispatch(getNotes(range.from, range.to)).catch(showFieldError);
    }, [dispatch, range.from, range.to]);

    const resolvedFilters = useMemo(() => resolveNoteFilters(filters, fixedPlayerId), [filters, fixedPlayerId]);
    const visibleNotes = useMemo(() => filterNotes(notes, resolvedFilters), [notes, resolvedFilters]);

    const handleFilterChange = (patch: Partial<NoteFilterState>) => setFilters((current) => ({ ...current, ...patch }));

    const handleClear = () => {
        setFilters(EMPTY_NOTE_FILTERS);
        setRange(defaultNotesRange(timeZone));
    };

    const openDialog = (request: NoteDialogRequest = {}) => setDialog({ open: true, request });

    const closeDialog = () => setDialog((current) => ({ ...current, open: false }));

    const handleDelete = (note: Note): Promise<void> => dispatch(deleteNote(note.uuid)).catch(showFieldError);

    return (
        <div className="notes-tab">
            {view !== "attributes" && (
                <NotesFilters
                    filters={filters}
                    onChange={handleFilterChange}
                    range={range}
                    onRangeChange={setRange}
                    onClear={handleClear}
                    fixedPlayerId={fixedPlayerId}
                />
            )}
            <div className="notes-tab-head">
                {view !== "attributes" && (
                    <Typography.Text type="secondary">
                        {visibleNotes.length} {visibleNotes.length === 1 ? "note" : "notes"}
                    </Typography.Text>
                )}
                <div className="notes-tab-actions">
                    <Button icon={<PlayCircleOutlined />} onClick={() => setTutorialOpen(true)} data-attr="notes.notes-tab.open-tutorial">
                        Show Tutorial
                    </Button>
                    {/* The Attributes view has no entry here, so no option shows as selected on that view. */}
                    <Segmented options={VIEWS} value={view} onChange={setView} />
                    {permissions?.ManageNoteAttributes && (
                        <Button onClick={() => setView("attributes")} data-attr="notes.notes-tab.open-attributes">
                            Attributes + Tags
                        </Button>
                    )}
                    {canWrite && (
                        <Button type="primary" onClick={() => openDialog()} data-attr="notes.notes-tab.create">
                            + New note
                        </Button>
                    )}
                </div>
            </div>
            {(view === "notes" || view === "notes-2col") && (
                <NotesList
                    twoColumns={view === "notes-2col"}
                    notes={visibleNotes}
                    totalCount={notes.length}
                    loading={loading}
                    onDelete={handleDelete}
                    onEdit={(note) => openDialog({ note })}
                    showPlayer={fixedPlayerId === undefined}
                />
            )}
            {view === "visualizer" && (
                <Visualizer
                    notes={visibleNotes}
                    playerIds={resolvedFilters.playerIds ?? []}
                    columns={columns}
                    onColumnsChange={setColumns}
                    presetName={presetName}
                    onPresetNameChange={setPresetName}
                    options={options}
                    onOptionsChange={setOptions}
                    loading={loading}
                    onOpenDialog={openDialog}
                />
            )}
            {view === "attributes" && (
                <div className="notes-settings">
                    <AttributeSettings />
                    <TagSettings />
                </div>
            )}
            <NotesTutorial isOpen={tutorialOpen} setOpen={setTutorialOpen} />
            <NoteDialog
                open={dialog.open}
                {...dialog.request}
                lockedPlayerId={dialog.request.lockedPlayerId ?? fixedPlayerId}
                onClose={closeDialog}
            />
        </div>
    );
};

const NoNotesAccess = () => {
    const permissionsManagerList: { first_name: string; last_name: string }[] = useSelector(
        ({ team }: any) => team?.info?.permissions_manager_list ?? [],
    );
    return (
        <div className="notes-no-access">
            <LockOutlined className="notes-no-access-icon" />
            <Typography.Title level={4} className="notes-no-access-title">
                Notes access required
            </Typography.Title>
            <Typography.Text type="secondary">
                You do not have permission to view notes.
                <br />
                Please reach out to your team's permissions manager to request access.
            </Typography.Text>
            {permissionsManagerList.length > 0 && (
                <Typography.Text>
                    Permissions Manager{permissionsManagerList.length > 1 ? "s" : ""}:{" "}
                    <strong>{permissionsManagerList.map((u) => `${u.first_name} ${u.last_name}`).join(", ")}</strong>
                </Typography.Text>
            )}
        </div>
    );
};

// The tab always shows. Nothing renders until the permissions load, so a permitted user never sees the lock.
// Without access the content never mounts, so no notes request fires.
export const NotesTab = (props: NotesTabProps) => {
    const userPermissions = useUserPermissions();
    const canViewNotes = useCanViewNotes();
    if (userPermissions == null) return null;
    return canViewNotes ? <NotesTabContent {...props} /> : <NoNotesAccess />;
};
